import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Plus,
  Pencil,
  Trash2,
  RefreshCw,
  X,
  Tag,
  CalendarDays,
} from "lucide-react";
import { safeFetch } from "@/api/safeFetch";

interface Promocion {
  codigo_promocional_id: number;
  codigo: string;
  porcentaje_descuento: number;
  fecha_expiracion: string | null;
  profesion_id: number | null;
  tipo_promocion: "GENERAL" | "ESPECIFICO";
  usos: number;
}

interface Profesion {
  profesion_id: number;
  nombre_profesion: string;
}

interface FormularioPromocion {
  codigo: string;
  porcentaje_descuento: string;
  fecha_expiracion: string;
  profesion_id: string;
  tipo_promocion: "GENERAL" | "ESPECIFICO";
}

const FORMULARIO_VACIO: FormularioPromocion = {
  codigo: "",
  porcentaje_descuento: "",
  fecha_expiracion: "",
  profesion_id: "",
  tipo_promocion: "GENERAL",
};

function normalizarLista<T>(respuesta: unknown, claves: string[]): T[] {
  if (Array.isArray(respuesta)) return respuesta as T[];
  if (respuesta && typeof respuesta === "object") {
    const objeto = respuesta as Record<string, unknown>;
    for (const clave of claves) {
      if (Array.isArray(objeto[clave])) return objeto[clave] as T[];
    }
  }
  return [];
}

function fechaCaducada(fecha: string | null): boolean {
  if (!fecha) return false;
  const hoy = new Date();
  hoy.setHours(0, 0, 0, 0);
  const caducidad = new Date(`${fecha.slice(0, 10)}T00:00:00`);
  return caducidad < hoy;
}

function fechaVisible(fecha: string | null): string {
  if (!fecha) return "Sin caducidad";
  const valor = new Date(`${fecha.slice(0, 10)}T00:00:00`);
  return Number.isNaN(valor.getTime())
    ? fecha
    : valor.toLocaleDateString("es-ES");
}

export default function PromocionesGenerales() {
  const navigate = useNavigate();
  const [promociones, setPromociones] = useState<Promocion[]>([]);
  const [profesiones, setProfesiones] = useState<Profesion[]>([]);
  const [cargando, setCargando] = useState(true);
  const [busqueda, setBusqueda] = useState("");
  const [filtroEstado, setFiltroEstado] = useState<
    "TODAS" | "ACTIVAS" | "CADUCADAS"
  >("TODAS");
  const [modalAbierto, setModalAbierto] = useState(false);
  const [promocionEditando, setPromocionEditando] = useState<number | null>(
    null,
  );
  const [formulario, setFormulario] =
    useState<FormularioPromocion>(FORMULARIO_VACIO);
  const [guardando, setGuardando] = useState(false);
  const [eliminandoId, setEliminandoId] = useState<number | null>(null);
  const [error, setError] = useState("");
  const [mensaje, setMensaje] = useState("");

  const cargarDatos = useCallback(async () => {
    setCargando(true);
    setError("");
    try {
      const [promocionesRespuesta, profesionesRespuesta] = await Promise.all([
        safeFetch.json<unknown>("/api/admin/promociones"),
        safeFetch.json<unknown>("/api/profesiones"),
      ]);
      setPromociones(
        normalizarLista<Promocion>(promocionesRespuesta, [
          "promociones",
          "data",
        ]),
      );
      setProfesiones(
        normalizarLista<Profesion>(profesionesRespuesta, [
          "profesiones",
          "data",
        ]),
      );
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "No se han podido cargar las promociones. Comprueba la conexión con el servidor.",
      );
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => {
    void cargarDatos();
  }, [cargarDatos]);

  const promocionesFiltradas = useMemo(() => {
    const termino = busqueda.trim().toLowerCase();
    return promociones.filter((promocion) => {
      const coincideTexto =
        !termino ||
        promocion.codigo.toLowerCase().includes(termino) ||
        (profesiones
          .find((p) => p.profesion_id === promocion.profesion_id)
          ?.nombre_profesion.toLowerCase()
          .includes(termino) ??
          false);

      const caducada = fechaCaducada(promocion.fecha_expiracion);
      const coincideEstado =
        filtroEstado === "TODAS" ||
        (filtroEstado === "CADUCADAS" && caducada) ||
        (filtroEstado === "ACTIVAS" && !caducada);

      return coincideTexto && coincideEstado;
    });
  }, [promociones, profesiones, busqueda, filtroEstado]);

  const abrirCrear = () => {
    setPromocionEditando(null);
    setFormulario(FORMULARIO_VACIO);
    setError("");
    setMensaje("");
    setModalAbierto(true);
  };

  const abrirEditar = (promocion: Promocion) => {
    setPromocionEditando(promocion.codigo_promocional_id);
    setFormulario({
      codigo: promocion.codigo,
      porcentaje_descuento: String(promocion.porcentaje_descuento),
      fecha_expiracion: promocion.fecha_expiracion?.slice(0, 10) ?? "",
      profesion_id:
        promocion.profesion_id === null ? "" : String(promocion.profesion_id),
      tipo_promocion: promocion.tipo_promocion ?? "GENERAL",
    });
    setError("");
    setMensaje("");
    setModalAbierto(true);
  };

  const actualizarCampo = <K extends keyof FormularioPromocion>(
    campo: K,
    valor: FormularioPromocion[K],
  ) => {
    setFormulario((actual) => ({ ...actual, [campo]: valor }));
  };

  const guardarPromocion = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError("");
    setMensaje("");

    const porcentaje = Number(formulario.porcentaje_descuento);
    if (!formulario.codigo.trim()) {
      setError("Introduce un código promocional.");
      return;
    }
    if (!Number.isFinite(porcentaje) || porcentaje <= 0 || porcentaje > 100) {
      setError(
        "El descuento debe ser un número mayor que 0 y menor o igual que 100.",
      );
      return;
    }
    if (
      formulario.tipo_promocion === "ESPECIFICO" &&
      !formulario.profesion_id
    ) {
      setError("Selecciona una profesión para esta promoción específica.");
      return;
    }

    const datos = {
      codigo: formulario.codigo.trim().toUpperCase(),
      porcentaje_descuento: porcentaje,
      fecha_expiracion: formulario.fecha_expiracion || null,
      profesion_id: formulario.profesion_id
        ? Number(formulario.profesion_id)
        : null,
      tipo_promocion: formulario.tipo_promocion,
    };

    setGuardando(true);
    try {
      if (promocionEditando !== null) {
        await safeFetch.json(`/api/admin/promociones/${promocionEditando}`, {
          method: "PUT",
          body: datos,
        });
        setMensaje("Promoción actualizada correctamente.");
      } else {
        await safeFetch.json("/api/admin/promociones", {
          method: "POST",
          body: datos,
        });
        setMensaje("Promoción creada correctamente.");
      }
      setModalAbierto(false);
      await cargarDatos();
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "No se ha podido guardar la promoción. Comprueba el backend.",
      );
    } finally {
      setGuardando(false);
    }
  };

  const eliminarPromocion = async (promocion: Promocion) => {
    const confirmado = window.confirm(
      `¿Seguro que quieres eliminar la promoción "${promocion.codigo}"?`,
    );
    if (!confirmado) return;

    setError("");
    setMensaje("");
    setEliminandoId(promocion.codigo_promocional_id);
    try {
      await safeFetch.json(
        `/api/admin/promociones/${promocion.codigo_promocional_id}`,
        { method: "DELETE" },
      );
      setPromociones((actuales) =>
        actuales.filter(
          (item) =>
            item.codigo_promocional_id !== promocion.codigo_promocional_id,
        ),
      );
      setMensaje(`La promoción ${promocion.codigo} se ha eliminado.`);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "No se ha podido eliminar la promoción. Comprueba el backend.",
      );
    } finally {
      setEliminandoId(null);
    }
  };

  const nombreProfesion = (id: number | null) => {
    if (id === null) return "Todas las profesiones";
    return (
      profesiones.find((p) => p.profesion_id === id)?.nombre_profesion ??
      "Profesión no encontrada"
    );
  };

  const abrirCalendario = () => {
    const input = document.getElementById(
      "caducidad-promocion",
    ) as HTMLInputElement | null;

    if (!input) return;

    if (typeof input.showPicker === "function") {
      input.showPicker();
    } else {
      input.focus();
      input.click();
    }
  };

  return (
    <main className="min-h-screen bg-zinc-50 px-4 py-6 sm:px-6 lg:px-8 pt-20">
      <div className="mx-auto max-w-7xl">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <div>
            <button
              type="button"
              onClick={() => navigate("/afiliados")}
              className="mb-3 text-sm font-medium text-zinc-600 hover:text-zinc-900"
            >
              ← Volver a gestión de afiliados
            </button>
            <h1 className="flex items-center gap-3 text-2xl font-bold text-zinc-900">
              <Tag className="h-7 w-7 text-purple-700" />
              Promociones generales
            </h1>
            <p className="mt-1 text-sm text-zinc-600">
              Crea, consulta y administra los códigos promocionales.
            </p>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => void cargarDatos()}
              disabled={cargando}
              className="inline-flex items-center gap-2 rounded-lg border border-zinc-300 bg-white px-4 py-2.5 text-sm font-semibold text-zinc-700 hover:bg-zinc-100 disabled:opacity-50"
            >
              <RefreshCw
                className={`h-4 w-4 ${cargando ? "animate-spin" : ""}`}
              />
              Actualizar
            </button>
            <button
              type="button"
              onClick={abrirCrear}
              className="inline-flex items-center gap-2 rounded-lg bg-purple-700 px-4 py-2.5 text-sm font-semibold text-white hover:bg-purple-800"
            >
              <Plus className="h-4 w-4" />
              Nueva promoción
            </button>
          </div>
        </div>

        {error && (
          <div
            role="alert"
            className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
          >
            {error}
            {error.toLowerCase().includes("404") && (
              <p className="mt-1">
                Es posible que las rutas administrativas de promociones aún no
                estén implementadas en el backend.
              </p>
            )}
          </div>
        )}
        {mensaje && (
          <div
            role="status"
            className="mb-4 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800"
          >
            {mensaje}
          </div>
        )}

        <section className="mb-5 grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div className="rounded-xl border border-zinc-200 bg-white p-4">
            <p className="text-sm text-zinc-500">Promociones encontradas</p>
            <p className="mt-1 text-2xl font-bold text-zinc-900">
              {promocionesFiltradas.length}
            </p>
          </div>
          <div className="rounded-xl border border-zinc-200 bg-white p-4">
            <p className="text-sm text-zinc-500">Activas (sin caducar)</p>
            <p className="mt-1 text-2xl font-bold text-emerald-700">
              {
                promociones.filter((p) => !fechaCaducada(p.fecha_expiracion))
                  .length
              }
            </p>
          </div>
          <div className="rounded-xl border border-zinc-200 bg-white p-4">
            <p className="text-sm text-zinc-500">Caducadas</p>
            <p className="mt-1 text-2xl font-bold text-red-700">
              {
                promociones.filter((p) => fechaCaducada(p.fecha_expiracion))
                  .length
              }
            </p>
          </div>
        </section>

        <section className="mb-4 flex flex-col gap-3 rounded-xl border border-zinc-200 bg-white p-4 sm:flex-row">
          <input
            value={busqueda}
            onChange={(event) => setBusqueda(event.target.value)}
            placeholder="Buscar por código o profesión..."
            className="min-w-0 flex-1 rounded-lg border border-zinc-300 px-3 py-2 text-sm outline-none focus:border-purple-600 focus:ring-2 focus:ring-purple-100"
          />
          <select
            value={filtroEstado}
            onChange={(event) =>
              setFiltroEstado(event.target.value as typeof filtroEstado)
            }
            className="rounded-lg border border-zinc-300 bg-white px-3 py-2 text-sm outline-none focus:border-purple-600 text-zinc-700"
          >
            <option value="TODAS">Todos los estados</option>
            <option value="ACTIVAS">Sin caducar</option>
            <option value="CADUCADAS">Caducadas</option>
          </select>
        </section>

        {cargando && promociones.length === 0 ? (
          <div className="rounded-xl border border-zinc-200 bg-white p-10 text-center text-zinc-600">
            Cargando promociones...
          </div>
        ) : promocionesFiltradas.length === 0 ? (
          <div className="rounded-xl border border-zinc-200 bg-white p-10 text-center">
            <Tag className="mx-auto mb-3 h-8 w-8 text-zinc-400" />
            <p className="font-semibold text-zinc-800">
              No hay promociones para mostrar
            </p>
            <p className="mt-1 text-sm text-zinc-500">
              Prueba a cambiar la búsqueda o crea una promoción nueva.
            </p>
          </div>
        ) : (
          <>
            <div className="hidden overflow-hidden rounded-xl border border-zinc-200 bg-white md:block">
              <table className="w-full text-left text-sm">
                <thead className="bg-zinc-100 text-xs uppercase tracking-wide text-zinc-600">
                  <tr>
                    <th className="px-4 py-3">Código</th>
                    <th className="px-4 py-3">Descuento</th>
                    <th className="px-4 py-3">Profesión</th>
                    <th className="px-4 py-3">Caducidad</th>
                    <th className="px-4 py-3">Usos</th>
                    <th className="px-4 py-3">Estado</th>
                    <th className="px-4 py-3 text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100">
                  {promocionesFiltradas.map((promocion) => {
                    const caducada = fechaCaducada(promocion.fecha_expiracion);
                    return (
                      <tr
                        key={promocion.codigo_promocional_id}
                        className="hover:bg-zinc-50"
                      >
                        <td className="px-4 py-3 font-semibold text-zinc-900">
                          {promocion.codigo}
                        </td>
                        <td className="px-4 py-3">
                          {Number(promocion.porcentaje_descuento)}%
                        </td>
                        <td className="px-4 py-3">
                          {nombreProfesion(promocion.profesion_id)}
                        </td>
                        <td className="px-4 py-3">
                          {fechaVisible(promocion.fecha_expiracion)}
                        </td>
                        <td className="px-4 py-3">{promocion.usos ?? 0}</td>
                        <td className="px-4 py-3">
                          <span
                            className={`rounded-full px-2.5 py-1 text-xs font-semibold ${caducada ? "bg-red-100 text-red-700" : "bg-emerald-100 text-emerald-700"}`}
                          >
                            {caducada ? "Caducada" : "Activa"}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex justify-end gap-2">
                            <button
                              type="button"
                              onClick={() => abrirEditar(promocion)}
                              aria-label={`Editar ${promocion.codigo}`}
                              className="rounded-md p-2 text-zinc-600 hover:bg-purple-50 hover:text-purple-700"
                            >
                              <Pencil className="h-4 w-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => void eliminarPromocion(promocion)}
                              disabled={
                                eliminandoId === promocion.codigo_promocional_id
                              }
                              aria-label={`Eliminar ${promocion.codigo}`}
                              className="rounded-md p-2 text-red-600 hover:bg-red-50 disabled:opacity-50"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="space-y-3 md:hidden">
              {promocionesFiltradas.map((promocion) => {
                const caducada = fechaCaducada(promocion.fecha_expiracion);
                return (
                  <article
                    key={promocion.codigo_promocional_id}
                    className="rounded-xl border border-zinc-200 bg-white p-4 shadow-sm"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <h2 className="font-bold text-zinc-900">
                          {promocion.codigo}
                        </h2>
                        <p className="mt-1 text-lg font-semibold text-purple-700">
                          {Number(promocion.porcentaje_descuento)}% de descuento
                        </p>
                      </div>
                      <span
                        className={`rounded-full px-2.5 py-1 text-xs font-semibold ${caducada ? "bg-red-100 text-red-700" : "bg-emerald-100 text-emerald-700"}`}
                      >
                        {caducada ? "Caducada" : "Activa"}
                      </span>
                    </div>
                    <dl className="mt-3 grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <dt className="text-zinc-500">Profesión</dt>
                        <dd className="mt-1 text-zinc-800">
                          {nombreProfesion(promocion.profesion_id)}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-zinc-500">Caducidad</dt>
                        <dd className="mt-1 text-zinc-800">
                          {fechaVisible(promocion.fecha_expiracion)}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-zinc-500">Usos</dt>
                        <dd className="mt-1 text-zinc-800">
                          {promocion.usos ?? 0}
                        </dd>
                      </div>
                    </dl>
                    <div className="mt-4 flex gap-2 border-t border-zinc-100 pt-3">
                      <button
                        type="button"
                        onClick={() => abrirEditar(promocion)}
                        className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg border border-zinc-300 px-3 py-2 text-sm font-semibold text-zinc-700 hover:bg-zinc-50"
                      >
                        <Pencil className="h-4 w-4" /> Editar
                      </button>
                      <button
                        type="button"
                        onClick={() => void eliminarPromocion(promocion)}
                        disabled={
                          eliminandoId === promocion.codigo_promocional_id
                        }
                        className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg border border-red-200 px-3 py-2 text-sm font-semibold text-red-700 hover:bg-red-50 disabled:opacity-50"
                      >
                        <Trash2 className="h-4 w-4" /> Eliminar
                      </button>
                    </div>
                  </article>
                );
              })}
            </div>
          </>
        )}
      </div>

      {modalAbierto && (
        <div className="fixed inset-x-0 bottom-0 top-20 z-[60] flex items-center justify-center overflow-y-auto bg-black/60 p-3 sm:p-6">
          <section
            role="dialog"
            aria-modal="true"
            aria-labelledby="promocion-modal-title"
            className="my-auto flex max-h-[calc(100dvh-6.5rem)] w-full max-w-xl flex-col overflow-y-auto rounded-2xl border border-zinc-200 bg-white text-zinc-900 shadow-2xl"
          >
            {/* Cabecera del formulario */}
            <div className="sticky top-0 z-10 flex shrink-0 items-center justify-between border-b border-zinc-200 bg-white px-5 py-4">
              <div>
                <h2
                  id="promocion-modal-title"
                  className="text-lg font-bold text-zinc-900"
                >
                  {promocionEditando === null
                    ? "Nueva promoción"
                    : "Editar promoción"}
                </h2>
                <p className="mt-1 text-sm text-zinc-500">
                  Completa los datos de la promoción.
                </p>
              </div>

              <button
                type="button"
                onClick={() => setModalAbierto(false)}
                aria-label="Cerrar formulario"
                className="ml-3 rounded-lg p-2 text-zinc-600 transition hover:bg-zinc-100 hover:text-zinc-900"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form
              onSubmit={guardarPromocion}
              className="space-y-5 p-5 text-zinc-900 sm:p-6"
            >
              {error && (
                <div
                  role="alert"
                  className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-800"
                >
                  {error}
                </div>
              )}
              <div>
                <label
                  htmlFor="codigo-promocion"
                  className="mb-1.5 block text-sm font-semibold text-zinc-800"
                >
                  Código promocional *
                </label>
                <input
                  id="codigo-promocion"
                  type="text"
                  required
                  maxLength={50}
                  autoComplete="off"
                  value={formulario.codigo}
                  onChange={(event) =>
                    actualizarCampo("codigo", event.target.value)
                  }
                  placeholder="Ej. BIENVENIDA10"
                  className="block w-full rounded-lg border border-zinc-300 bg-white px-3 py-3 text-base text-zinc-900 placeholder:text-zinc-400 shadow-sm outline-none transition focus:border-purple-600 focus:ring-2 focus:ring-purple-200"
                />
              </div>
              <div>
                <label
                  htmlFor="porcentaje-promocion"
                  className="mb-1.5 block text-sm font-semibold text-zinc-800"
                >
                  Descuento (%) *
                </label>
                <input
                  id="porcentaje-promocion"
                  type="number"
                  required
                  min="0.01"
                  max="100"
                  step="0.01"
                  value={formulario.porcentaje_descuento}
                  onChange={(event) =>
                    actualizarCampo("porcentaje_descuento", event.target.value)
                  }
                  placeholder="10"
                  className="block w-full rounded-lg border border-zinc-300 bg-white px-3 py-3 text-base text-zinc-900 placeholder:text-zinc-400 shadow-sm outline-none transition focus:border-purple-600 focus:ring-2 focus:ring-purple-200"
                />
                <p className="mt-1 text-xs text-zinc-500">
                  Introduce un valor superior a 0 y hasta el 100 %.
                </p>
              </div>
              <div>
                <label
                  htmlFor="tipo-promocion"
                  className="mb-1.5 block text-sm font-semibold text-zinc-800"
                >
                  Ámbito de la promoción *
                </label>
                <select
                  id="tipo-promocion"
                  value={formulario.tipo_promocion}
                  onChange={(event) =>
                    actualizarCampo(
                      "tipo_promocion",
                      event.target
                        .value as FormularioPromocion["tipo_promocion"],
                    )
                  }
                  className="block w-full appearance-auto rounded-lg border border-zinc-300 bg-white px-3 py-3 text-base text-zinc-900 shadow-sm outline-none focus:border-purple-600 focus:ring-2 focus:ring-purple-200"
                >
                  <option className="bg-white text-zinc-900" value="GENERAL">
                    General (todas las profesiones)
                  </option>
                  <option className="bg-white text-zinc-900" value="ESPECIFICO">
                    Específica (una profesión)
                  </option>
                </select>
              </div>
              {formulario.tipo_promocion === "ESPECIFICO" && (
                <div>
                  <label
                    htmlFor="profesion-promocion"
                    className="mb-1.5 block text-sm font-semibold text-zinc-800"
                  >
                    Profesión *
                  </label>
                  <select
                    id="profesion-promocion"
                    required
                    value={formulario.profesion_id}
                    onChange={(event) =>
                      actualizarCampo("profesion_id", event.target.value)
                    }
                    className="block w-full rounded-lg border border-zinc-300 bg-white px-3 py-3 text-base text-zinc-900 shadow-sm outline-none focus:border-purple-600 focus:ring-2 focus:ring-purple-200"
                  >
                    <option className="bg-white text-zinc-900" value="">
                      Selecciona una profesión
                    </option>
                    {profesiones.map((profesion) => (
                      <option
                        key={profesion.profesion_id}
                        value={profesion.profesion_id}
                        className="bg-white text-zinc-900"
                      >
                        {profesion.nombre_profesion}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div>
                <label
                  htmlFor="caducidad-promocion"
                  className="mb-1.5 block text-sm font-semibold text-zinc-800"
                >
                  Fecha de caducidad
                </label>

                <div className="relative">
                  <input
                    id="caducidad-promocion"
                    type="date"
                    value={formulario.fecha_expiracion}
                    onChange={(event) =>
                      actualizarCampo("fecha_expiracion", event.target.value)
                    }
                    className="block w-full cursor-pointer rounded-lg border border-zinc-300 bg-white px-3 py-3 pr-12 text-base text-zinc-900 shadow-sm outline-none transition focus:border-purple-600 focus:ring-2 focus:ring-purple-200"
                  />

                  <button
                    type="button"
                    onClick={abrirCalendario}
                    aria-label="Abrir calendario"
                    className="absolute right-3 top-1/2 z-10 -translate-y-1/2 rounded-md p-1 text-zinc-500 hover:bg-purple-50 hover:text-purple-700"
                  >
                    <CalendarDays className="h-5 w-5" aria-hidden="true" />
                  </button>
                </div>

                <p className="mt-1.5 text-xs text-zinc-500">
                  Deja este campo vacío si la promoción no debe caducar.
                </p>
              </div>

              <div className="flex flex-col-reverse gap-3 border-t border-zinc-200 pt-5 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={() => setModalAbierto(false)}
                  className="inline-flex min-h-11 items-center justify-center rounded-lg border border-zinc-300 bg-white px-5 py-2.5 text-sm font-semibold text-zinc-700 transition hover:bg-zinc-100"
                >
                  Cancelar
                </button>

                <button
                  type="submit"
                  disabled={guardando}
                  className="inline-flex min-h-11 items-center justify-center rounded-lg bg-purple-700 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-purple-800 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {guardando
                    ? "Guardando..."
                    : promocionEditando === null
                      ? "Crear promoción"
                      : "Guardar cambios"}
                </button>
              </div>
            </form>
          </section>
        </div>
      )}
    </main>
  );
}
